import firebase from "firebase/compat/app";
import moment from "../utils/app_moment";
import db, { fxs } from "../utils/firestore";
import {
    AgeCategory,
    AvailabilityStatus,
    ClinicianMethodology,
    ConvFluentExpLevel,
    DateType,
    IAvailabilitySchedule,
    IAvailabilityWindow,
    IClient,
    IClientBooking,
    IClinician,
    IClinicianStatusChange,
    IExperienceLevel,
    IIntakeAvailability,
    IOfficeWindow,
    ISearchParams,
    LowHighExperienceLevel,
    OfficeRequestCategory,
    SignatureData,
    UpdatedFrom,
    YesNoExperienceLevel,
} from "intake_shared/src/types";
import { displayPhoneNumber, getStringVariations } from "../utils/utils";
import { Collection } from "intake_shared/src/enum";
import { isExperienceCredentialExpired } from "intake_shared/src/util";
import { uniqBy } from "lodash";
import { arrayUnion, Timestamp } from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import {
    sendDeletedOfficeWindowActionEmails,
    sendOfficeWindowDeleteRequestActionEmails,
} from "../utils/queue_email";

function cleanupClinicianData(clinician: IClinician): IClinician {
    const availabilityFieldsToKeep: (keyof IClientBooking)[] = [
        "client_id",
        "clinician_display_name",
        "clinician_id",
        "client_display_name",
        "requested_service",
    ];
    // TODO: remove Hack because too lazy to figure out why availability is duplicating.
    clinician.available = uniqBy(clinician.available, "id");
    for (const availability of clinician.available) {
        for (const field of availabilityFieldsToKeep) {
            delete (availability as any)[field];
        }
    }
    return clinician;
}

export async function updateClinicianData({
    clinician_id,
    changes = {},
    confirmed_bookings,
    held_bookings,
    available_schedules,
}: {
    clinician_id: string;
    changes?: { [key: string]: any };
    confirmed_bookings?: IClientBooking[];
    held_bookings?: IClientBooking[];
    available_schedules?: IAvailabilitySchedule[];
}) {
    const clinicianChanges = { ...changes };
    if (confirmed_bookings) {
        clinicianChanges["confirmed"] = confirmed_bookings;
    }
    if (held_bookings) {
        clinicianChanges["held"] = held_bookings;
    }
    if (available_schedules) {
        clinicianChanges["available"] = available_schedules;
    }
    if (Object.keys(clinicianChanges).length < 1) {
        return;
    }
    await db
        .collection(Collection.CLINICIANS)
        .doc(clinician_id)
        .update({
            ...clinicianChanges,
            updated_at: new Date(),
            updated_from: UpdatedFrom.INTAKE,
        });
}

export async function saveClinicianData(
    clinician: IClinician,
    is_new = false,
    allMethodologyOptions: string[] = Object.values(ClinicianMethodology),
    changedBy?: string,
    priorityChangeNote?: string
) {
    const true_methodologies = clinician.methodology.reduce(
        (prev: { [key: string]: boolean }, curr: string) => {
            prev[curr] = true;
            return prev;
        },
        {}
    );
    const false_methodologies = allMethodologyOptions
        .filter((m) => !clinician.methodology.includes(m))
        .reduce((prev: { [key: string]: boolean }, curr: string) => {
            prev[curr] = false;
            return prev;
        }, {});
    const false_flat_locations = Object.values(Location)
        .filter((m) => !clinician.locations.includes(m))
        .reduce((prev: { [key: string]: boolean }, curr: string) => {
            prev[curr] = false;
            return prev;
        }, {});
    const true_flat_locations = clinician.locations.reduce(
        (prev: { [key: string]: boolean }, curr: string) => {
            prev[curr] = true;
            return prev;
        },
        {}
    );
    if (is_new) {
        clinician.office_windows = [];
        clinician.available = [];
        clinician.unavailable = [];
        clinician.held = [];
        clinician.confirmed = [];
        clinician.booking_paused = true;
    }
    const {
        experience_levels,
        booking_pause_reason,
        booking_paused_by,
        booking_paused_at,
        ...clinicianFields
    } = cleanupClinicianData(clinician);
    const clinician_doc = {
        ...clinicianFields,
        firstname: clinician.firstname.trim(),
        lastname: clinician.lastname.trim(),
        npi: clinician.npi || "",
        email: clinician.email.trim(),
        dob: Timestamp.fromDate(clinician.dob as Date),
        start_date: Timestamp.fromDate(clinician.start_date as Date),
        started_practicing: clinician.started_practicing
            ? Timestamp.fromDate(clinician.started_practicing as Date)
            : null,
        updated_at: Timestamp.fromDate(new Date()),
        contact_info_client: displayPhoneNumber(clinician.contact_info_client),
        contact_info_personal: displayPhoneNumber(clinician.contact_info_personal),
        do_not_show_on_website: clinician.do_not_show_on_website,
        do_not_sync_to_zocdoc: clinician.do_not_sync_to_zocdoc,
        ideal_caseload: clinician.ideal_caseload || null,
        ...true_methodologies,
        ...false_methodologies,
        ...true_flat_locations,
        ...false_flat_locations,
        updated_from: UpdatedFrom.INTAKE,
    };

    const clinicianRef = db.collection(Collection.CLINICIANS).doc(clinician.email);
    await db.runTransaction(async (transaction) => {
        const snapshot = await transaction.get(clinicianRef);
        const clinicianFieldsToSave = { ...clinician_doc };
        const existingClinician = snapshot.data() as IClinician | undefined;
        const isNewPause =
            clinician.booking_paused && !existingClinician?.booking_paused;
        const bookingPauseChanged =
            Boolean(clinician.booking_paused) !==
            Boolean(existingClinician?.booking_paused);
        const priorityChanged =
            Boolean(clinician.marked_as_priority) !==
            Boolean(existingClinician?.marked_as_priority);
        const trimmedPriorityNote = priorityChangeNote?.trim();
        if (priorityChanged && (!trimmedPriorityNote || !changedBy)) {
            throw new Error(
                "A priority change note and signed-in user are required"
            );
        }
        if (isNewPause) {
            const reason =
                is_new && !booking_pause_reason?.trim()
                    ? "Automatically paused when clinician was created."
                    : booking_pause_reason?.trim();
            if (!reason || !changedBy) {
                throw new Error("A pause reason and signed-in user are required");
            }
            Object.assign(clinicianFieldsToSave, {
                booking_pause_reason: reason,
                booking_paused_by: changedBy,
                booking_paused_at: firebase.firestore.FieldValue.serverTimestamp(),
            });
        }
        const statusChanges: Pick<
            IClinicianStatusChange,
            "field" | "enabled" | "note"
        >[] = [];
        const addStatusChange = (
            field: IClinicianStatusChange["field"],
            enabled: boolean,
            note: string
        ) => {
            statusChanges.push({ field, enabled, note });
        };
        if (bookingPauseChanged) {
            if (!changedBy) {
                throw new Error("A signed-in user is required");
            }
            addStatusChange(
                "booking_paused",
                Boolean(clinician.booking_paused),
                isNewPause
                    ? is_new && !booking_pause_reason?.trim()
                        ? "Automatically paused when clinician was created."
                        : booking_pause_reason?.trim() || ""
                    : ""
            );
        }
        if (priorityChanged) {
            addStatusChange(
                "marked_as_priority",
                Boolean(clinician.marked_as_priority),
                trimmedPriorityNote || ""
            );
        }
        if (is_new) {
            const flattenedExperienceLevels: Record<string, boolean> = {};
            for (const key in experience_levels) {
                flattenedExperienceLevels[key] = hasExperienceLevel(
                    experience_levels[key].level
                );
            }
            transaction.set(clinicianRef, {
                ...clinicianFieldsToSave,
                experience_levels,
                ...flattenedExperienceLevels,
            });
        } else {
            transaction.set(clinicianRef, clinicianFieldsToSave, { merge: true });
        }
        statusChanges.forEach((change) => {
            const changeRef = db
                .collection(Collection.CLINICIAN_STATUS_CHANGES)
                .doc();
            transaction.set(changeRef, {
                clinician_id: clinician.email,
                ...change,
                changed_by: changedBy,
                changed_at: firebase.firestore.FieldValue.serverTimestamp(),
            });
        });
    });
}

export async function fetchClinicianStatusChanges(
    clinicianId: string
): Promise<IClinicianStatusChange[]> {
    const snapshot = await db
        .collection(Collection.CLINICIAN_STATUS_CHANGES)
        .where("clinician_id", "==", clinicianId)
        .get();
    return snapshot.docs
        .map((doc) => {
            const data = doc.data();
            return {
                id: doc.id,
                clinician_id: data.clinician_id,
                field: data.field,
                enabled: data.enabled,
                note: data.note || "",
                changed_by: data.changed_by,
                changed_at: data.changed_at?.toDate(),
            } as IClinicianStatusChange;
        })
        .sort((a, b) => b.changed_at.getTime() - a.changed_at.getTime());
}

function convertStoreDocToClinician(doc: any): IClinician {
    const doc_data = doc.data();
    if (!doc_data) {
        return {} as IClinician;
    }

    return {
        id: doc.id,
        email: doc_data.email,
        fullname: `${doc_data.firstname} ${doc_data.lastname}` || doc_data.fullname,
        firstname: doc_data.firstname || doc_data.fullname,
        lastname: doc_data.lastname || "",
        credential: doc_data.credential,
        npi: doc_data.npi,
        contact_info_client: doc_data.contact_info_client,
        contact_info_personal: doc_data.contact_info_personal,
        personality_notes: doc_data.personality_notes,
        dob: doc_data.dob ? doc_data.dob.toDate() : undefined,
        start_date: doc_data.start_date ? doc_data.start_date.toDate() : undefined,
        updated_at: doc_data.updated_at ? doc_data.updated_at.toDate() : undefined,
        locations: doc_data.locations,
        supervisor_id: doc_data.supervisor_id,
        secondary_supervisor_ids: doc_data.secondary_supervisor_ids || [],
        methodology: doc_data.methodology,
        office_windows: doc_data.office_windows,
        experience_levels: doc_data.experience_levels,
        available: uniqBy((doc_data.available || []) as IAvailabilityWindow[], "id"),
        do_not_show_on_website: doc_data.do_not_show_on_website || false,
        do_not_sync_to_zocdoc: doc_data.do_not_sync_to_zocdoc,
        ideal_caseload: doc_data.ideal_caseload || null,
        last_synced_to_zocdoc: doc_data.last_synced_to_zocdoc || null,
        clinician_calendar_id: doc_data.clinician_calendar_id || null,
        unavailable: uniqBy(
            (doc_data.unavailable || []) as IAvailabilityWindow[],
            "id"
        ),
        held: uniqBy((doc_data.held || []) as IClientBooking[], "id"),
        confirmed: uniqBy((doc_data.confirmed || []) as IClientBooking[], "id"),
        is_supervisor: Boolean(doc_data.is_supervisor),
        started_practicing: doc_data.started_practicing
            ? doc_data.started_practicing.toDate()
            : undefined,
        booking_paused: doc_data.booking_paused || false,
        booking_pause_reason: doc_data.booking_pause_reason,
        booking_paused_by: doc_data.booking_paused_by,
        booking_paused_at: doc_data.booking_paused_at?.toDate(),
        marked_as_priority: doc_data.marked_as_priority || false,
        departments: doc_data.departments || [],
        available_for_intake_sessions: doc_data.available_for_intake_sessions || [],
    };
}

export async function fetchAllCliniciansIdentifier(): Promise<
    { id: string; first_name: string; last_name: string }[]
> {
    const clincian_query = await db
        .collection(Collection.CLINICIANS)
        .orderBy("firstname", "asc")
        .get();
    const clinician_list = clincian_query.docs.map((doc) => {
        const clinician = doc.data();
        return {
            id: clinician.email,
            first_name: clinician.firstname,
            last_name: clinician.lastname,
        };
    });
    return clinician_list;
}

export async function fetchAllCliniciansData(
    afterId?: string,
    beforeId?: string,
    limit = 100
): Promise<IClinician[]> {
    let next_records_query = db
        .collection(Collection.CLINICIANS)
        .orderBy("firstname", "asc");
    if (!afterId && !beforeId) {
        next_records_query = next_records_query.limit(limit);
    }
    if (afterId) {
        next_records_query = next_records_query.startAfter(afterId).limit(limit);
    }
    if (beforeId) {
        next_records_query = next_records_query
            .endBefore(beforeId)
            .limitToLast(limit);
    }
    const raw_data = (await next_records_query.get()).docs;
    const clinicians: IClinician[] = raw_data.map(convertStoreDocToClinician);
    return clinicians;
}

export async function findClinicianByField(
    field: keyof IClinician,
    value: string | boolean
) {
    let query: any = db.collection(Collection.CLINICIANS);
    if (typeof value === "string") {
        const name =
            value === "kalisa" || value === "Kalisa" || value === "KALISA"
                ? "KaLisa"
                : value;
        const values = getStringVariations(name);
        query = query.where(field, "in", values);
    } else if (typeof value === "boolean") {
        query = query.where(field, "==", value);
    }
    const raw_data = (await (await query).get()).docs;
    const clinicians: IClinician[] = raw_data.map(convertStoreDocToClinician);
    return clinicians;
}

export async function fetchAllCliniciansByLocation(
    location: string
): Promise<IClinician[]> {
    const raw_data = (
        await (
            await db
                .collection(Collection.CLINICIANS)
                .where("locations", "array-contains", location)
        ).get()
    ).docs;
    const clinicians: IClinician[] = raw_data.map(convertStoreDocToClinician);
    return clinicians;
}

export async function fetchAllSupervisors(): Promise<IClinician[]> {
    const raw_data = (
        await (
            await db
                .collection(Collection.CLINICIANS)
                .where("is_supervisor", "==", true)
        ).get()
    ).docs;
    const clinicians: IClinician[] = raw_data.map(convertStoreDocToClinician);
    return clinicians;
}

export async function fetchSupervisees(supervisorId: string): Promise<IClinician[]> {
    // Secondary supervisors are stored as objects, which Firestore cannot match
    // by a single field, so one full read is needed. Primary supervisees come
    // from the same snapshot instead of a second query that re-reads them.
    const allCliniciansSnapshot = await db.collection(Collection.CLINICIANS).get();

    const allClinicians = allCliniciansSnapshot.docs.map((doc) =>
        convertStoreDocToClinician(doc)
    );
    const primarySupervisees = allClinicians.filter(
        (clinician) => clinician.supervisor_id === supervisorId
    );

    // Filter clinicians where the supervisorId is in the secondary_supervisor_ids array
    const secondarySupervisees = allClinicians.filter((clinician) =>
        clinician.secondary_supervisor_ids?.some(
            (supervisor) => supervisor.supervisor_id === supervisorId
        )
    );

    const allSupervisees = [...primarySupervisees, ...secondarySupervisees];

    return allSupervisees;
}

export async function doesClinicianExist(email: string): Promise<boolean> {
    const clinician = await db.collection(Collection.CLINICIANS).doc(email).get();
    return clinician.exists;
}

export async function getClinicianByEmail(
    email: string
): Promise<IClinician | undefined> {
    const clinician_doc = await db
        .collection(Collection.CLINICIANS)
        .doc(email)
        .get();
    if (clinician_doc.exists) {
        return convertStoreDocToClinician(clinician_doc);
    }
    return;
}

export async function deleteClinician(clinicianId: string, deletedBy?: string) {
    if (deletedBy) {
        await saveDeletedClinicianData(clinicianId, deletedBy);
    }

    await db.collection(Collection.CLINICIANS).doc(clinicianId).delete();
}

export async function saveDeletedClinicianData(
    clinicianEmail: string,
    deletedBy: string,
    deletedAt: Date = new Date()
) {
    const deletedClinicianData = {
        email: clinicianEmail,
        deleted_by: deletedBy,
        deleted_at: Timestamp.fromDate(deletedAt),
    };

    await db
        .collection(Collection.DELETED_CLINICIANS)
        .doc(clinicianEmail)
        .set(deletedClinicianData);
}

export async function addClinicianOfficeWindow({
    clinicianId,
    id,
    office,
    start,
    end,
    location,
    ending_date,
}: {
    clinicianId: string;
    id: string;
    office: string;
    start: string;
    end: string;
    location: string;
    ending_date?: string;
}) {
    const endingDate = ending_date || "";
    await db
        .collection(Collection.CLINICIANS)
        .doc(clinicianId)
        .update({
            office_windows: arrayUnion({
                id,
                office,
                start,
                end,
                location,
                endingDate,
            }),
            locations: arrayUnion(location),
            updated_from: UpdatedFrom.INTAKE,
        });
}

export async function addClinicianAvailability(
    clinicianId: string,
    status: AvailabilityStatus,
    schedule: IAvailabilitySchedule
) {
    await db
        .collection(Collection.CLINICIANS)
        .doc(clinicianId)
        .update({
            [status]: arrayUnion({ ...schedule }),
            updated_from: UpdatedFrom.INTAKE,
        });
}

export async function updateClinicianOfficeWindows(
    clinicianId: string,
    office_windows: IOfficeWindow[],
    deletedBy: "clinician" | "office_manager",
    scheduleToDelete?: IAvailabilitySchedule,
    clinicianName?: string,
    reasonForDeleting?: string,
    type?: string,
    intakeSignature?: SignatureData
) {
    await db
        .collection(Collection.CLINICIANS)
        .doc(clinicianId)
        .update({ office_windows, updated_from: UpdatedFrom.INTAKE });

    const office_request_details = {
        ...scheduleToDelete,
        location: scheduleToDelete?.location as string,
        office: scheduleToDelete?.office as string,
        start_date: scheduleToDelete?.start as DateType,
        end_date: scheduleToDelete?.end as DateType,
        category: "delete" as OfficeRequestCategory,
        clinician_name: clinicianName,
        reason: reasonForDeleting,
    };

    // send emails

    if (type === "delete_office_window_list") {
        sendOfficeWindowDeleteRequestActionEmails(
            clinicianId as string,
            office_request_details,
            "Approved",
            intakeSignature || ({} as SignatureData)
        );
    } else if (
        scheduleToDelete?.location !== "Remote" &&
        type !== "delete_office_window_list"
    ) {
        sendDeletedOfficeWindowActionEmails(
            clinicianId as string,
            office_request_details,
            deletedBy,
            intakeSignature || ({} as SignatureData)
        );
    }
}

// function to update all clinicians office window and availability using the office window the office manager is about to delete
export async function removeClinicianOfficeWindowAndAvailability(
    matchingClinicians: { clinicianId: string; schedules: { id: string }[] }[],
    matchingSchedulesForResponse: {
        clinicianId: string;
        schedules: { id: string }[];
    }[]
) {
    for (const matchingClinician of matchingClinicians) {
        const { clinicianId, schedules } = matchingClinician;
        const matchingScheduleData = matchingSchedulesForResponse.find(
            (data) => data.clinicianId === clinicianId
        );
        if (!matchingScheduleData) {
            continue;
        }
        const { schedules: scheduleData } = matchingScheduleData;

        // Fetch the clinician's current document from the database
        const clinicianDocRef = db
            .collection(Collection.CLINICIANS)
            .doc(clinicianId);
        const clinicianDoc = await clinicianDocRef.get();

        if (!clinicianDoc.exists) {
            continue;
        }

        const clinicianData = clinicianDoc.data() as {
            office_windows: IOfficeWindow[];
            available: IAvailabilitySchedule[];
        };

        // Filter out the schedules separately from both office_windows and available arrays
        clinicianData.office_windows = clinicianData.office_windows.filter(
            (officeWindow) => {
                return !schedules.some(
                    (schedule) => schedule.id === officeWindow.id
                );
            }
        );

        clinicianData.available = clinicianData.available.filter((availability) => {
            return !scheduleData.some((schedule) => schedule.id === availability.id);
        });

        // Update the clinician's data in the database
        await clinicianDocRef.update({
            office_windows: clinicianData.office_windows,
            available: clinicianData.available,
            updated_from: UpdatedFrom.INTAKE,
        });
    }
}

export async function updateClinicianAvailability(
    clinicianId: string,
    status: AvailabilityStatus,
    schedules: IAvailabilitySchedule[]
) {
    await db
        .collection(Collection.CLINICIANS)
        .doc(clinicianId)
        .update({ [status]: [...schedules], updated_from: UpdatedFrom.INTAKE });
}

export async function updateAvailabilitySchedule(
    clinicianId: string,
    appointmentId: string,
    booking_changes: { [key: string]: any }
) {
    const clinician_doc_ref = db.collection(Collection.CLINICIANS).doc(clinicianId);

    await db.runTransaction(async (transaction) => {
        const clinician_doc = await transaction.get(clinician_doc_ref);
        if (!clinician_doc.exists) {
            throw new Error("Error: Cannot find clinician or client");
        }

        const clinician = (await clinician_doc.data()) as IClinician;

        const available_window = clinician.available.find(
            (a) => a.id === appointmentId
        );
        if (!available_window) {
            throw new Error("Error: Cannot find available window");
        }
        const updated_booking = {
            ...(available_window || {}),
            ...booking_changes,
        };
        const updatedAvailableAppt = { ...available_window, ...updated_booking };
        const availableAppts = clinician.available.map((appt: any) => {
            if (appt.id === appointmentId) {
                appt = updatedAvailableAppt;
            }
            return appt;
        });
        transaction.update(clinician_doc_ref, {
            available: availableAppts,
            updated_from: UpdatedFrom.INTAKE,
        });
    });
}

export async function updateIntakeSessionBooking(
    supervisorId: string,
    intakeSessionId: string,
    booking_id: string,
    client_id: string,
    client_display_name: string,
    status: "held" | "confirmed" | "available",
    practice_id: string,
    supervisee_display_name: string
) {
    const supervisor_doc_ref = db
        .collection(Collection.CLINICIANS)
        .doc(supervisorId);

    await db.runTransaction(async (transaction) => {
        const supervisor_doc = await transaction.get(supervisor_doc_ref);
        if (!supervisor_doc.exists) {
            console.error("Error: Cannot find supervisor");
        }

        const supervisor = (await supervisor_doc.data()) as IClinician;
        const intakeSessions = supervisor.available_for_intake_sessions || [];

        const intakeSession = intakeSessions.find(
            (session) => session.id === intakeSessionId
        );
        if (!intakeSession) {
            console.error("Error: Cannot find intake session");
        }

        const updatedIntakeSessions = intakeSessions.map((session: any) => {
            if (session.id === intakeSessionId) {
                return {
                    ...session,
                    booking_id,
                    client_id,
                    client_display_name,
                    status,
                    practice_id,
                    supervisees_display_name: supervisee_display_name,
                };
            }
            return session;
        });

        transaction.update(supervisor_doc_ref, {
            available_for_intake_sessions: updatedIntakeSessions,
            updated_from: UpdatedFrom.INTAKE,
        });
    });
}

export async function updateIntakeSessionStatusByBooking(
    transaction: any,
    supervisorId: string,
    bookingId: string,
    clientId: string,
    status: "held" | "confirmed" | "available"
) {
    const supervisor_doc_ref = db
        .collection(Collection.CLINICIANS)
        .doc(supervisorId);

    const supervisor_doc = await transaction.get(supervisor_doc_ref);

    if (!supervisor_doc.exists) {
        console.error("Error: Cannot find supervisor");
    }

    const supervisor = supervisor_doc.data() as IClinician;
    const intakeSessions = supervisor.available_for_intake_sessions || [];

    const intakeSession = intakeSessions.find(
        (session) =>
            session.booking_id === bookingId && session.client_id === clientId
    );

    if (!intakeSession) {
        console.error("Error: Cannot find intake session");
    }

    const updatedIntakeSessions = intakeSessions.map((session: any) => {
        if (session.id === intakeSession?.id) {
            return { ...session, status };
        }
        return session;
    });

    transaction.update(supervisor_doc_ref, {
        available_for_intake_sessions: updatedIntakeSessions,
        updated_from: UpdatedFrom.INTAKE,
    });
}

export async function replaceIntakeSessionBooking(
    oldSupervisorId: string,
    bookingId: string,
    clientId: string,
    newSupervisorId: string,
    newIntakeSessionId: string,
    newClientDisplayName: string,
    status: "held" | "confirmed" | "available",
    practice_id: string,
    supervisee_display_name: string
) {
    const oldSupervisor_doc_ref = db
        .collection(Collection.CLINICIANS)
        .doc(oldSupervisorId);

    await db.runTransaction(async (transaction) => {
        const oldSupervisor_doc = await transaction.get(oldSupervisor_doc_ref);

        if (!oldSupervisor_doc.exists) {
            console.error("Error: Cannot find old supervisor");
        }

        const oldSupervisor = (await oldSupervisor_doc.data()) as IClinician;

        const oldIntakeSessions = oldSupervisor.available_for_intake_sessions || [];

        const oldIntakeSession = oldIntakeSessions.find(
            (session) =>
                session.booking_id === bookingId && session.client_id === clientId
        );

        if (!oldIntakeSession) {
            console.error("Error: Cannot find old intake session to clear");
        }

        if (oldSupervisorId === newSupervisorId) {
            const newIntakeSession = oldIntakeSessions.find(
                (session: any) => session.id === newIntakeSessionId
            );
            if (!newIntakeSession) {
                console.error("Error: Cannot find new intake session");
            }

            // Update both sessions in one operation: clear old, book new
            const finalUpdatedSessions = oldIntakeSessions.map((session: any) => {
                if (session.id === oldIntakeSession?.id) {
                    // Clear the old session
                    return {
                        ...session,
                        booking_id: "",
                        client_id: "",
                        client_display_name: "",
                        status: "available",
                        practice_id: "",
                    };
                } else if (session.id === newIntakeSessionId) {
                    return {
                        ...session,
                        booking_id: bookingId,
                        client_id: clientId,
                        client_display_name: newClientDisplayName,
                        status,
                        practice_id,
                        supervisees_display_name: supervisee_display_name,
                    };
                }
                return session;
            });

            // Single update for same supervisor
            transaction.update(oldSupervisor_doc_ref, {
                available_for_intake_sessions: finalUpdatedSessions,
                updated_from: UpdatedFrom.INTAKE,
            });
        } else {
            // Different supervisors: clear old supervisor slot and book new supervisor slot
            const newSupervisor_doc_ref = db
                .collection(Collection.CLINICIANS)
                .doc(newSupervisorId);

            const newSupervisor_doc = await transaction.get(newSupervisor_doc_ref);
            if (!newSupervisor_doc.exists) {
                console.error("Error: Cannot find new supervisor");
            }

            const newSupervisor = (await newSupervisor_doc.data()) as IClinician;
            const newIntakeSessions =
                newSupervisor.available_for_intake_sessions || [];

            const newIntakeSession = newIntakeSessions.find(
                (session) => session.id === newIntakeSessionId
            );
            if (!newIntakeSession) {
                console.error("Error: Cannot find new intake session");
            }

            const updatedOldIntakeSessions = oldIntakeSessions.map(
                (session: any) => {
                    if (session.id === oldIntakeSession?.id) {
                        return {
                            ...session,
                            booking_id: "",
                            client_id: "",
                            client_display_name: "",
                            status: "available",
                            practice_id: "",
                        };
                    }
                    return session;
                }
            );

            const updatedNewIntakeSessions = newIntakeSessions.map(
                (session: any) => {
                    if (session.id === newIntakeSessionId) {
                        return {
                            ...session,
                            booking_id: bookingId,
                            client_id: clientId,
                            client_display_name: newClientDisplayName,
                            status,
                            practice_id,
                            supervisees_display_name: supervisee_display_name,
                        };
                    }
                    return session;
                }
            );

            transaction.update(oldSupervisor_doc_ref, {
                available_for_intake_sessions: updatedOldIntakeSessions,
                updated_from: UpdatedFrom.INTAKE,
            });

            transaction.update(newSupervisor_doc_ref, {
                available_for_intake_sessions: updatedNewIntakeSessions,
                updated_from: UpdatedFrom.INTAKE,
            });
        }
    });
}

/**
 * Remove intake session from supervisor's available_for_intake_sessions
 * when a booking with that intake session is cancelled
 */
export async function removeIntakeSessionFromSupervisor(
    supervisorId: string,
    bookingId: string,
    clientId: string
) {
    const supervisor_doc_ref = db
        .collection(Collection.CLINICIANS)
        .doc(supervisorId);

    await db.runTransaction(async (transaction) => {
        const supervisor_doc = await transaction.get(supervisor_doc_ref);
        if (!supervisor_doc.exists) {
            console.error("Error: Cannot find supervisor");
        }

        const supervisor = (await supervisor_doc.data()) as IClinician;
        const intakeSessions = supervisor.available_for_intake_sessions || [];

        const updatedIntakeSessions = intakeSessions.map(
            (session: IIntakeAvailability) => {
                if (
                    session.booking_id === bookingId &&
                    session.client_id === clientId
                ) {
                    return {
                        ...session,
                        booking_id: "",
                        client_id: "",
                        client_display_name: "",
                        status: "available" as const,
                    };
                }
                return session;
            }
        );

        transaction.update(supervisor_doc_ref, {
            available_for_intake_sessions: updatedIntakeSessions,
            updated_from: UpdatedFrom.INTAKE,
        });
    });
}

function hasExperienceLevel(
    level?: LowHighExperienceLevel | YesNoExperienceLevel | ConvFluentExpLevel
) {
    return Boolean(
        level &&
            level !== LowHighExperienceLevel.NONE &&
            level !== YesNoExperienceLevel.NO
    );
}

export async function updateClinicianExperienceLevels(
    clinicianId: string,
    baseExperienceLevels: IExperienceLevel,
    experience_levels: IExperienceLevel
) {
    const updateExperienceLevels = httpsCallable(
        fxs,
        "httpFunctionUpdateClinicianExperienceLevels"
    );
    await updateExperienceLevels({
        clinicianId,
        baseExperienceLevels,
        experienceLevels: experience_levels,
    });
}

function getDateBetweenAges(start_age: number, end_age: number) {
    return {
        start_date: moment().startOf("day").subtract(end_age, "years").toDate(),
        end_date: moment().startOf("day").subtract(start_age, "years").toDate(),
    };
}

export async function fetchCliniciansByIds(ids: string[]): Promise<IClinician[]> {
    let clinicians: IClinician[] = [];
    while (ids.length > 0) {
        const id_set = ids.splice(0, 10);
        const raw_data = (
            await (
                await db.collection(Collection.CLINICIANS).where("id", "in", id_set)
            ).get()
        ).docs;
        clinicians = clinicians.concat(raw_data.map(convertStoreDocToClinician));
    }
    return clinicians;
}

export async function searchClincians(
    search_params: ISearchParams
): Promise<IClinician[]> {
    let clincians_query: any = db.collection(Collection.CLINICIANS);
    let clinicians_results: IClinician[] = [];
    if (search_params.experience_level) {
        Object.keys(search_params.experience_level).forEach((k) => {
            if (search_params.experience_level[k] === true) {
                clincians_query = clincians_query.where(
                    k,
                    "==",
                    search_params.experience_level[k]
                );
            }
        });
    }

    if (search_params.methodology) {
        Object.keys(search_params.methodology).forEach((k) => {
            if (search_params.methodology[k] === true) {
                clincians_query = clincians_query.where(
                    k,
                    "==",
                    search_params.methodology[k]
                );
            }
        });
    }

    if (clincians_query) {
        const raw_data = (await clincians_query.get()).docs;
        clinicians_results = raw_data.map(convertStoreDocToClinician);
    } else {
        clinicians_results = await fetchAllCliniciansData();
    }

    const selectedExperienceLevels = Object.entries(
        search_params.experience_level || {}
    )
        .filter(([, selected]) => selected)
        .map(([experienceLevel]) => experienceLevel);
    if (selectedExperienceLevels.length > 0) {
        clinicians_results = clinicians_results.filter((clinician) =>
            selectedExperienceLevels.every(
                (experienceLevel) =>
                    hasExperienceLevel(
                        clinician.experience_levels?.[experienceLevel]?.level
                    ) &&
                    !isExperienceCredentialExpired(
                        clinician.experience_levels?.[experienceLevel]
                    )
            )
        );
    }

    if (search_params.search_locations.length) {
        clinicians_results = clinicians_results.filter((c) =>
            (c.office_windows || []).some((officeWindow) => {
                const windowLocation = (officeWindow.location || "")
                    .trim()
                    .toLowerCase();
                return (
                    windowLocation.length > 0 &&
                    search_params.search_locations.includes(windowLocation)
                );
            })
        );
    }

    if (search_params?.search_departments?.length) {
        clinicians_results = clinicians_results.filter((c) =>
            c?.departments?.some((d) =>
                search_params?.search_departments?.includes(d.toLowerCase())
            )
        );
    }

    if (search_params.search_credentials.length) {
        clinicians_results = clinicians_results.filter((c) =>
            search_params.search_credentials.includes(c.credential.toLowerCase())
        );
    }

    if (search_params.first_name) {
        const name_variations = getStringVariations(search_params.first_name);
        const name_regex = new RegExp(`^(${name_variations.join("|")})$`, "i");
        clinicians_results = clinicians_results.filter((c) =>
            name_regex.test(c.firstname)
        );
    }

    if (search_params.age_categories.length) {
        let dates: { start_date?: Date; end_date?: Date } = {};
        const ageRanges: number[] = [];
        if (search_params.age_categories.includes(AgeCategory.TWENTIES)) {
            ageRanges.push(20, 30);
        }

        if (search_params.age_categories.includes(AgeCategory.THIRTIES)) {
            ageRanges.push(30, 40);
        }

        if (search_params.age_categories.includes(AgeCategory.FOURTIES)) {
            ageRanges.push(40, 50);
        }

        if (search_params.age_categories.includes(AgeCategory.FIFTIES)) {
            ageRanges.push(50, 60);
        }

        if (search_params.age_categories.includes(AgeCategory.OVER_FIFTIES)) {
            ageRanges.push(60, 200);
        }

        ageRanges.sort((a, b) => (a > b ? 1 : -1));
        dates = getDateBetweenAges(ageRanges[0], ageRanges[ageRanges.length - 1]);

        clincians_query = clincians_query
            .where("dob", ">=", dates.start_date)
            .where("dob", "<=", dates.end_date);

        clinicians_results = clinicians_results.filter((c) => {
            const cDobTime = c.dob?.getTime() || 0;
            const startDobTime = dates.start_date?.getTime() || 0;
            const endDobTime = dates.end_date?.getTime() || 0;
            return cDobTime >= startDobTime && cDobTime <= endDobTime;
        });
    }

    return clinicians_results;
}

export async function updateClinicianAndClientDocuments(
    officeName: string,
    editedOfficeName: string
): Promise<void> {
    const [cliniciansSnapshot, clientsSnapshot] = await Promise.all([
        db.collection(Collection.CLINICIANS).get(),
        db.collection(Collection.CLIENTS).get(),
    ]);
    const clinicianFields = [
        "confirmed",
        "held",
        "office_windows",
        "available",
        "unavailable",
    ] as const;
    const clientFields = ["held_bookings", "confirmed_bookings"] as const;
    const clinicianIds = cliniciansSnapshot.docs
        .filter((doc) => {
            const clinician = doc.data() as IClinician;
            return clinicianFields.some(
                (field) =>
                    Array.isArray(clinician[field]) &&
                    clinician[field].some((entry) => entry.office === officeName)
            );
        })
        .map((doc) => doc.id);
    const clientIds = clientsSnapshot.docs
        .filter((doc) => {
            const client = doc.data() as IClient;
            return clientFields.some(
                (field) =>
                    Array.isArray(client[field]) &&
                    client[field].some((entry) => entry.office === officeName)
            );
        })
        .map((doc) => doc.id);
    const renameOffice = httpsCallable(fxs, "httpFunctionRenameOfficeInBookings");
    for (let index = 0; index < clinicianIds.length; index += 100) {
        await renameOffice({
            officeName,
            editedOfficeName,
            clinicianIds: clinicianIds.slice(index, index + 100),
            clientIds: [],
        });
    }
    for (let index = 0; index < clientIds.length; index += 100) {
        await renameOffice({
            officeName,
            editedOfficeName,
            clinicianIds: [],
            clientIds: clientIds.slice(index, index + 100),
        });
    }
}
